import { Card, Col, Form, message, Row } from "antd";
import { isAxiosError } from "axios";
import dayjs from "dayjs";
import React from "react";
import { useForm } from "react-hook-form";
import { DateField, TextAreaField } from "../../../../atoms/FormElement";
import { InputNumberFieldV2 as InputNumberField } from "../../../../atoms/FormElement/InputNumberField";
import { getInputPlaceholderMessage, getValidationProps } from "../../../../libs/form";
import { getMinErrorMessage } from "../../../../libs/form/utils/getMinErrorMessage";
import { formatDateByType } from "../../../../libs/utils";
import { ApprovalBar } from "../../../../molecules/ApprovalBar";
import { ButtonGroup, ButtonGroupProps } from "../../../../molecules/Button/ButtonGroup";
import { Invoice } from "../types";
import { AddCreditNoteRequest, useAddCreditNote, useApproveCreditNote } from "./api";

type FieldType = {
  dueAmount: number;
  amountToCredit: number;
  dueBalance: number;
  creditDate: dayjs.Dayjs;
  note: string;
};

type ApplyCreditNoteProps = {
  invoice?: Invoice;
  actionType?: "apply" | "approveReject";
  onClose?: () => void;
  onSuccess?: () => void;
};

export const ApplyCreditNote = (props: ApplyCreditNoteProps) => {
  const { invoice, actionType = "apply", onClose, onSuccess } = props;
  const { pdcId, totalAmount, balance, creditNoteApprovalEnabled, creditNote } = invoice ?? {};
  const { creditAmount, amountAfterCredit, creditDate, note, status } = creditNote ?? {};

  const isApprovalPending = status === "pending";

  const [approvalDecision, setApprovalDecision] = React.useState<"approve" | "reject" | null>(null);

  const onError = (error) => {
    if (isAxiosError(error)) message.error(error?.response?.data?.error);
  };

  const { addCreditNoteRequest, isPendingAddCreditNoteRequest } = useAddCreditNote({ onSuccess, onError });
  const { approveCreditNoteRequest, isPendingApproveCreditNoteRequest } = useApproveCreditNote({ onSuccess, onError });

  const form = useForm<FieldType>({
    defaultValues: {
      dueAmount: balance !== undefined && balance > 0 ? balance : (totalAmount ?? 0),
      amountToCredit: creditAmount ?? 0,
      dueBalance: amountAfterCredit ?? balance ?? totalAmount ?? 0,
      creditDate: creditDate ? dayjs(creditDate) : dayjs(),
      note: note ?? "",
    },
  });

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = form;

  const watchedDueAmount = watch("dueAmount");
  const amountToCreditFieldName = "amountToCredit";

  const onSubmit = handleSubmit(async (data) => {
    if (!pdcId) return;
    const { amountToCredit, creditDate, note } = data;

    const creditNote: AddCreditNoteRequest["creditNote"] = {
      amountToCredit: Number(amountToCredit),
      creditDate: formatDateByType(creditDate, "api"),
      note,
    };

    await addCreditNoteRequest({ pdcId, creditNote });
  });

  const handleAmountChange = (value: string | number | null) => {
    const balance = Number(watchedDueAmount) - Number(value || 0);
    setValue("dueBalance", Math.max(0, balance), { shouldValidate: true });
  };

  const isApprovalFlow = actionType === "approveReject";
  const isApprovalBarVisible = isApprovalFlow && approvalDecision !== null;
  const approvalStatus =
    approvalDecision === "approve" ? "approved" : approvalDecision === "reject" ? "rejected" : null;
  const approvalMessage =
    approvalDecision === "reject"
      ? "Please confirm if you would like to proceed with the rejection."
      : "Please confirm if you would like to proceed with the approval.";

  const handleApproveConfirm = () => {
    if (!pdcId || !creditNote?.creditNoteId || !approvalStatus) return;
    approveCreditNoteRequest({
      pdcId,
      creditNoteId: creditNote.creditNoteId,
      approvalStatus,
    });
  };

  const approvalPrimaryButton: ButtonGroupProps["primaryButton"] = {
    label: "Approve",
    onClick: () => setApprovalDecision("approve"),
    disabled: isPendingApproveCreditNoteRequest || isApprovalBarVisible,
  };

  const approvalSecondaryButton: ButtonGroupProps["secondaryButton"] = {
    label: "Reject",
    onClick: () => setApprovalDecision("reject"),
    disabled: isPendingApproveCreditNoteRequest || isApprovalBarVisible,
  };

  const submitPrimaryButton: ButtonGroupProps["primaryButton"] = {
    label: creditNoteApprovalEnabled ? "Send for Approval" : "Submit",
    htmlType: "submit",
    loading: isPendingAddCreditNoteRequest,
  };

  const submitSecondaryButton: ButtonGroupProps["secondaryButton"] = {
    label: "Cancel",
    onClick: onClose,
    disabled: isPendingAddCreditNoteRequest,
  };

  const primaryButton = isApprovalFlow ? approvalPrimaryButton : submitPrimaryButton;
  const secondaryButton = isApprovalFlow ? approvalSecondaryButton : submitSecondaryButton;

  return (
    <Card>
      <ApprovalBar
        isVisible={isApprovalBarVisible}
        message={approvalMessage}
        containerStyle={{ marginBottom: 24 }}
        primaryButton={{
          label: "Yes",
          loading: isPendingApproveCreditNoteRequest,
          onClick: handleApproveConfirm,
        }}
        secondaryButton={{
          label: "No",
          onClick: () => setApprovalDecision(null),
        }}
      />
      <Form name="applyCreditNote" layout="vertical" className="bf-main" onFinish={onSubmit}>
        <Row gutter={24}>
          <Col flex="1">
            <InputNumberField fieldname="dueAmount" label="Due Amount" format="currency" control={control} disabled />
          </Col>
          <Col flex="1">
            <InputNumberField
              isRequired
              fieldname={amountToCreditFieldName}
              label="Amount To Credit"
              format="currency"
              control={control}
              disabled={isApprovalPending}
              onChange={handleAmountChange}
              rules={{
                validate: (value) => {
                  const numericValue = Number(value);

                  if (!numericValue || numericValue < 1) return getMinErrorMessage(null, "greater", 0);
                  if (numericValue > Number(watchedDueAmount)) return "Amount must not exceed the due balance";
                  return true;
                },
              }}
              inputNumberProps={{ placeholder: getInputPlaceholderMessage(amountToCreditFieldName) }}
            />
          </Col>
        </Row>

        <Row gutter={24}>
          <Col flex="1">
            <InputNumberField fieldname="dueBalance" label="Due Balance" format="currency" control={control} disabled />
          </Col>
          <Col flex="1">
            <DateField
              fieldname="creditDate"
              label="Credit Date"
              control={control}
              disabled={isApprovalPending}
              {...getValidationProps<FieldType>({ fieldName: "creditDate", errors, isRequired: true })}
            />
          </Col>
        </Row>

        <TextAreaField
          fieldname="note"
          label="Note"
          control={control}
          disabled={isApprovalPending}
          iProps={{ placeholder: getInputPlaceholderMessage("note") }}
        />
        <ButtonGroup primaryButton={primaryButton} secondaryButton={secondaryButton} />
      </Form>
    </Card>
  );
};
